import {
    EzObject,
    EzNumber,
    EzString
} from '/ezlibrary/helpers/EzHelpers.js';

import { EzSpinnerType } from '/public/webcomponents/spinner/EzSpinnerType.js';

/**
 * @class
 * @description
 * Information object for active spinners
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *  import { EzActiveSpinnerInfo } from '/public/webcomponents/spinner/EzActiveSpinnerInfo.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzActiveSpinnerInfo {
    /**
     * @static
     * @public @readonly @property
     * Gets the default parent container id constant: 'body'
     *
     * @returns {string}
     */
    static get DEFAULT_SPINNER_PARENT_CONTAINER_ID() {
        return 'body';
    }

    /**
     * @static
     * @readonly @property
     * Gets the default spinner id:
     *
     * @returns {string}
     */
    static get DEFAULT_SPINNER_ID() {
        return 'EzClockerDefaultSpinnerId';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets string constant: 'Please wait ...'
     *
     * @returns {string}
     */
    static get DEFAULT_SPINNER_MSG() {
        return 'Please wait ...';
    }

    /**
     * @static
     * @public @property
     * Gets the default spinner objects object
     *
     * @returns {object}
     */
    static get DEFAULT_SPINNER_OPTIONS() {
        const gearSvgEnumData = EzSpinnerType.ezEnumData(EzSpinnerType.GEAR_SVG);

        return {
            id: EzString.stringWithLengthOrDefault(
                EzSpinnerType.ezSpinnerConfig(EzSpinnerType.LINE_SPINNER_SVG)?.id,
                EzActiveSpinnerInfo.DEFAULT_SPINNER_ID),
            spinner: EzObject.assignOrDefault(
                EzSpinnerType.ezSpinnerConfig(EzSpinnerType.LINE_SPINNER_SVG),
                {
                    id: EzSpinnerType.GEAR_SVG,
                    display: gearSvgEnumData.displayName,
                    type: gearSvgEnumData.spinnerType,
                    assetUrl: gearSvgEnumData.assetUrl,
                    maxWidth: gearSvgEnumData.attributes.width,
                    minWidth: gearSvgEnumData.attributes.width,
                    width: gearSvgEnumData.attributes.width,
                    height: gearSvgEnumData.attributes.height,
                    canResize: gearSvgEnumData.attributes.canResize
                }),
            message: EzActiveSpinnerInfo.DEFAULT_SPINNER_MSG,
            parentContainerId: EzActiveSpinnerInfo.DEFAULT_SPINNER_PARENT_CONTAINER_ID
        };
    }

    /**
     * @private @field
     * Stores the active spinner's id
     *
     * @type {string}
     */
    #spinnerId = EzActiveSpinnerInfo.DEFAULT_SPINNER_ID;
    /**
     * @public @property @getter
     * Gets the active spinner's id
     *
     * @returns {string}
     */
    get spinnerId() {
        return this.#spinnerId;
    }
    /**
     * @public @property @gsetter
     * Sets the active spinner's id
     *
     * @param {undefined|null|string} spinnerId
     * Default: EzActiveSpinnerInfo.DEFAULT_SPINNER_ID
     */
    set spinnerId(spinnerId = EzActiveSpinnerInfo.DEFAULT_SPINNER_ID) {
        this.#spinnerId = EzString.stringWithLengthOrDefault(
            spinnerId,
            EzActiveSpinnerInfo.DEFAULT_SPINNER_ID);
    }

    /**
     * @private @field
     * Stores the active spinner's options
     *
     * @type {string}
     */
    #options = EzActiveSpinnerInfo.DEFAULT_SPINNER_OPTIONS;
    /**
     * @public @property @getter
     * Gets the active spinner's options
     *
     * @returns {string}
     */
    get options() {
        return this.#options;
    }
    /**
     * @public @property @gsetter
     * Sets the active spinner's options
     *
     * @param {undefined|null|string} spinnerId
     * Default: EzActiveSpinnerInfo.DEFAULT_SPINNER_ID
     */
    set options(options = EzActiveSpinnerInfo.DEFAULT_SPINNER_OPTIONS) {
        this.#options = EzObject.assignOrDefault(
            options,
            EzActiveSpinnerInfo.DEFAULT_SPINNER_OPTIONS);
    }

    /**
     * @private @field
     * Stores the active spinners 'active' count (number of calls to show spinner without closing)
     *
     * @type {string}
     */
    #activeCount = 1;
    /**
     * @public @property @getter
     * Gets the active spinners 'active' count (number of calls to show spinner without closing)
     *
     * @returns {string}
     */
    get activeCount() {
        return this.#activeCount;
    }
    /**
     * @public @property @gsetter
     * Sets the active spinners 'active' count (number of calls to show spinner without closing)
     *
     * @param {undefined|null|string} spinnerId
     * Default: EzActiveSpinnerInfo.DEFAULT_SPINNER_ID
     */
    set activeCount(activeCount = 1) {
        this.#spinnerId = EzNumber.numberOrDefault(
            activeCount,
            1);
    }
}
